import { Button, CheckIcon, TextInput, toast } from "@fla/components";
import { yupResolver } from "@hookform/resolvers/yup";
import { Modal } from "components";
import { serverTimestamp } from "firebase/firestore";
import { FirebaseRefs, GetStartedStoreTypes, SliceStatus } from "interfaces";
import { FC, useState } from "react";
import { useForm } from "react-hook-form";
import { FirestoreService, NotificationService } from "services";
import { logFirebaseAnalytics } from "utils";
import * as yup from "yup";

const schema = yup
  .object()
  .shape({
    user: yup.object().shape({
      first_name: yup.string().required("Required"),
      last_name: yup.string().required("Required"),
      phone: yup.string().required("Required"),
      email: yup.string().required("Required").email("Check the email")
    })
  })
  .required();

interface IProps {
  open: boolean;
  onClose: () => void;
  userId?: string;
  setEnquiryStatus: React.Dispatch<React.SetStateAction<SliceStatus>>;
  store: GetStartedStoreTypes;
  defaultValues?: any;
}

const UserDetailsModal: FC<IProps> = ({ open, onClose, userId, setEnquiryStatus, store, defaultValues }) => {
  const [status, setStatus] = useState(SliceStatus.Idle);

  const { handleSubmit, control } = useForm({
    mode: "onSubmit",
    defaultValues: {
      user: defaultValues
    },
    resolver: yupResolver(schema)
  });

  const onSubmit = async (values: {
    user: { first_name: string; last_name: string; email: string; phone: string };
  }) => {
    try {
      if (!userId) return;
      setStatus(SliceStatus.Pending);
      setEnquiryStatus(SliceStatus.Pending);

      await FirestoreService.updateDocument({
        coll: FirebaseRefs.INITIAL_DETAILS,
        docId: userId,
        data: { ...values, isCompleted: true, step: 3, updatedAt: serverTimestamp(), submittedAt: serverTimestamp() }
      });

      await NotificationService.sendQuoteNotification({
        subject:
          process.env.REACT_APP_NODE_ENV !== "production"
            ? `Splitwise Quote [${process.env.REACT_APP_NODE_ENV?.toUpperCase()}]`
            : `Splitwise Quote`,
        app_name: "splitwise",

        ...values,
        ...store
      });

      logFirebaseAnalytics("splitwise_app_quote_submit", {
        first_name: values.user.first_name,
        last_name: values.user.last_name,
        email: values.user.email,
        phone: values.user.phone,
        package: store.package,
        legal_fees: store.legal_fees
      });

      setStatus(SliceStatus.Resolved);
      setEnquiryStatus(SliceStatus.Resolved);
    } catch (error: any) {
      setStatus(SliceStatus.Rejected);
      setEnquiryStatus(SliceStatus.Rejected);
      toast({ message: error?.message, type: "error" });
    }
  };

  const isSubmitting = status === SliceStatus.Pending;

  return (
    <Modal classes="!px-10 !pt-5 !pb-10" isOpen={open} onClose={onClose} showCloseIcon={false}>
      {status === SliceStatus.Resolved ? (
        <article className="flex flex-col items-center space-y-10 py-10">
          <div className="flex flex-col items-center">
            <CheckIcon classes="w-20 h-20" strokeColor="fill-current text-green-600" />
            <h3 className="mt-4">Information Received</h3>
            <p className="tracking-wider">We’ll be in touch with you shortly.</p>
          </div>

          <Button
            type="button"
            bgColor="bg-primary"
            className="w-32 max-w-xs py-2 rounded-lg tracking-wider hover:bg-primary-faded"
            onClick={onClose}
          >
            Close
          </Button>
        </article>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <h2 className="max-w-sm mx-auto text-center my-5">Our Legal Team Will Call You to Get Started</h2>

          <p className="text-center mb-12">Your selection will be saved and sent to our team</p>
          <p className="font-bold mt-4 mb-2 text-sm">Your Name</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextInput
              title={<p className="font-light text-xs">First Name</p>}
              control={control}
              name="user.first_name"
              id="user.first_name"
              placeholder="First Name"
              wrapClasses="col-span-1"
            />
            <TextInput
              title={<p className="font-light text-xs">Last Name</p>}
              control={control}
              name="user.last_name"
              id="user.last_name"
              placeholder="Last Name"
              wrapClasses="col-span-1"
            />
          </div>

          <p className="font-bold mb-2 text-sm mt-5">Mobile Number</p>
          <TextInput
            control={control}
            name="user.phone"
            id="user.phone"
            placeholder="0401 111 111"
            wrapClasses="col-span-1"
          />

          <p className="font-bold mb-2 text-sm mt-5">Email Address</p>
          <TextInput
            control={control}
            name="user.email"
            id="user.email"
            placeholder="Email Address"
            wrapClasses="col-span-1"
          />

          <Button
            type="submit"
            bgColor="bg-primary"
            className="w-full py-2.5 mt-10 rounded-lg tracking-wider hover:bg-primary-faded"
            isSubmitting={isSubmitting}
          >
            Submit
          </Button>
        </form>
      )}
    </Modal>
  );
};
export default UserDetailsModal;
